import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, AlertCircle } from "lucide-react";

interface ProfileClient {
  clientId: number
  contractualQuantity: number;
  discount: number;
  materialUseName: string
  priceWithDiscount: number
  productCode: string
  productDescription: string
  productId: number
  productPrice: number
  priceWithoutDiscount?: number
  productSchoolYear: string;
  profileClientId: number;
  segmentName: string
  seriesName: string;
  totalWithDiscount: number;
  totalWithoutDiscount: number
}

interface RemoveProductDialogProps {
  client: {
    id: number;
    name: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (product: ProfileClient, motivo: string) => void;
  product: ProfileClient | null;
}

export const RemoveProductDialog = ({
  isOpen,
  onClose,
  onConfirm,
  product,
  client
}: RemoveProductDialogProps) => {
  const [motivo, setMotivo] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setMotivo("");
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!motivo.trim()) {
      setError("O motivo da remoção é obrigatório");
      return;
    }

    if (motivo.length > 250) {
      setError("O motivo deve ter no máximo 250 caracteres");
      return;
    }

    if (product) {
      onConfirm(product, motivo.trim());
      handleClose();
    }
  };

  const handleClose = () => {
    setError("");
    setMotivo("");
    onClose();
  };

  if (!product) return null;

  const remainingChars = 250 - motivo.length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Remover Produto
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>Cliente: </Label>
            <div className="flex items-center gap-2">
              {/* <Badge variant="outline">{product.}</Badge> */}
              <span className="font-medium">{client.name}</span>
            </div>
          </div>

          <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Badge variant="outline">{product.productCode}</Badge>
              {product.productDescription}
            </h4>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Utilização:</span>
                <span className="ml-2">{product.materialUseName || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Quantidade:</span>
                <span className="ml-2">{product.contractualQuantity || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Desconto:</span>
                <span className="ml-2">{product.discount ? `${product.discount}%` : "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Grupo:</span>
                <span className="ml-2">{product.seriesName || "N/A"}</span>
              </div>
            </div>

            {product.productPrice && (
              <div className="text-sm">
                <span className="text-muted-foreground">Valor unitário:</span>
                <span className="ml-2 font-semibold">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(product.priceWithoutDiscount ?? product.productPrice)}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="motivo" className="flex items-center gap-1 text-destructive">
              Motivo da Remoção <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, 250))}
              placeholder="Descreva o motivo para remover este produto (obrigatório)"
              maxLength={250}
              className={
                !motivo.trim()
                  ? "border-destructive focus-visible:ring-destructive"
                  : ""
              }
              rows={3}
            />
            <div className="flex justify-between text-xs">
              <span className="text-destructive">Campo obrigatório</span>
              <span className={remainingChars < 20 ? "text-destructive" : "text-muted-foreground"}>
                {remainingChars} caracteres restantes
              </span>
            </div>
          </div>

          <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Atenção!</p>
                <p className="text-sm text-destructive/80">
                  Esta ação removerá permanentemente o produto da lista do cliente. 
                  Certifique-se de que deseja continuar.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-destructive text-sm">{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="destructive"
              disabled={!motivo.trim()}
            >
              Confirmar Remoção
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};